import { Component, Event, EventEmitter, forceUpdate, h, Host, Prop, State, Watch } from "@stencil/core";
import { QuestionRadio } from "./question-radio";
import { i18nProvider } from "@trumpf/ux.i18n";
import loadTranslations, { TranslationKey } from "../../utils/loadTranslations";
import { Answers, questions, Question, firstQuestionKey, buildRequestData } from "../../api/proactive-case-closure";
import { QuestionComment } from "./question-comment";
import { request } from "../../utils/request";

const baseClass = "mf-proactive-case-closure";
/**
 * Micro-frontend component for the proactive case closure form
 * This form shows a series of questions to the user and collects the answers
 * depending on the answers the next question is displayed
 */
@Component({
    tag: "mf-proactive-case-closure",
    styleUrl: "proactive-case-closure.css",
    shadow: false
})
export class ProactiveCaseClosure {
    /** SIS case ID */
    @Prop() callId: string = "";
    /**
     * The locale to use for translations
     */
    @Prop({ reflect: true }) locale: string = "en-US";

    @State() questions: Question[] = [];
    @State() answers: Answers = {};
    /** indicates if all required answers were given */
    @State() complete: boolean = false;
    @State() submitting: boolean = false;
    @State() submitSuccess: boolean = false;
    @State() submitError: string = null;
    @State() authError: string = null;

    /**
     * Event that is fired when the user clicks the cancel button
     */
    @Event({
        eventName: "mfServiceCallCancel",
        bubbles: true,
        composed: true
    })
    cancelEvent: EventEmitter<void>;

    /**
     * Event that is fired when the user clicks the final OK button
     */
    @Event({
        eventName: "mfServiceCallSuccess",
        bubbles: true,
        composed: true
    })
    successEvent: EventEmitter<void>;

    private i18nSubscriberId: string;

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    componentWillLoad() {
        this.loadTranslations();
        this.updateQuestions();
        this.checkComplete();
    }

    @Watch("locale")
    onLocaleChange() {
        if (this.locale !== i18nProvider.language) {
            this.loadTranslations();
        }
    }

    private loadTranslations() {
        loadTranslations(this.locale).then((locale) => {
            i18nProvider.changeLanguage(locale);
            this.locale = locale;
        });
    }

    private submit() {
        this.submitting = true;
        const data = buildRequestData(this.answers, this.callId);
        request<unknown>("/servicecall/proactivecaseclosure", data)
            .then((response) => {
                // console.log("Request sent. Response:", response);
                if (response) {
                    this.submitSuccess = true;
                } else {
                    this.submitError = "An error occurred while submitting the form";
                }
                this.submitting = false;
            })
            .catch((error) => {
                this.submitError = error || "Unknown error";
                this.submitting = false;
            });
    }

    private cancel() {
        // console.log("cancel");
        this.cancelEvent.emit();
    }

    private finish() {
        // console.log("finish");
        this.successEvent.emit();
    }

    /**
     * this method collects the questions that can be displayed
     * We start at the initial question and get the next question based on the selected answer
     */
    @Watch("answers")
    private updateQuestions(): void {
        const questionsArray: Question[] = [questions[firstQuestionKey]];
        while (this.hasAnsweredLast(questionsArray) && this.lastAnswerHasNext(questionsArray)) {
            questionsArray.push(this.getNextQuestion(questionsArray[questionsArray.length - 1]));
        }
        this.updateAnswers(questionsArray);
        this.questions = questionsArray;
    }

    @Watch("answers")
    @Watch("questions")
    private checkComplete(): void {
        this.complete =
            this.hasAnsweredLast(this.questions) &&
            !this.lastAnswerHasNext(this.questions) &&
            this.hasAnsweredComment(this.questions);
    }

    private hasAnsweredLast(questionsArray: Question[]): boolean {
        return this.answers[questionsArray[questionsArray.length - 1].name] !== undefined;
    }

    private lastAnswerHasNext(questionsArray: Question[]): boolean {
        const lastQuestion = questionsArray[questionsArray.length - 1];
        return lastQuestion.options[this.answers[lastQuestion.name]].next !== undefined;
    }

    private getNextQuestion(currentQuestion: Question): Question {
        const currentAnswer = this.answers[currentQuestion.name];
        return questions[currentQuestion.options[currentAnswer].next];
    }

    private hasAnsweredComment(questionsArray: Question[]): boolean {
        const lastQuestion = questionsArray[questionsArray.length - 1];
        if (!lastQuestion.comment) {
            return true;
        }
        const commentAnswer = this.answers[lastQuestion.comment.name];
        return commentAnswer && commentAnswer.trim() !== "";
    }

    /** this method removes those answers where the associated question has been removed */
    private updateAnswers(questionsArray: Question[]): void {
        const answersObject = { ...this.answers };
        Object.keys(answersObject).forEach((key) => {
            if (
                !questionsArray.find((question) => question.name === key) &&
                !questionsArray.find((question) => question.comment?.name === key)
            ) {
                delete answersObject[key];
            }
        });
        if (JSON.stringify(answersObject) !== JSON.stringify(this.answers)) {
            this.answers = answersObject;
        }
    }

    private onAuth(e: CustomEvent): void {
        this.authError = e.detail.error;
    }

    render() {
        const t = (key: TranslationKey, options = undefined) => i18nProvider.t(`mf-service-calls:${String(key)}`, options);
        if (this.callId === "") {
            return <Host data-error="no callId provided" />;
        }

        if (this.submitSuccess) {
            return (
                <Host class={baseClass}>
                    <div class={`${baseClass}__container`}>
                        <h1 class="ux-text-title ux-text-title--big ux-u-text-align--center">
                            {t("serviceCase.proactiveCaseClosure.dialogTitle")}
                        </h1>
                        <ux-button onClick={() => this.finish()} variant="primary">
                            {t("serviceCase.proactiveCaseClosure.dialog.buttonOk")}
                        </ux-button>
                    </div>
                </Host>
            );
        }

        if (this.authError) {
            return (
                <Host class={baseClass}>
                    <div class={`${baseClass}__container`}>
                        <h1 class="ux-text-title ux-text-title--big ux-u-text-align--center">Error: not authenticated</h1>
                        <div class="ux-u-text-align--center">
                            <ux-button onClick={() => this.cancel()}>{t("button.cancel")}</ux-button>
                        </div>
                    </div>
                </Host>
            );
        }

        return (
            <Host class={baseClass}>
                <ux-u-silent-auth
                    clientId="1kffflasH4444COTiFmP8DDO9ywa"
                    issuer="https://identity-s.trumpf.com"
                    onUxAuth={(e) => this.onAuth(e)}
                />
                {/* TODO: this ^ needs to be different for prod */}
                <div class={`${baseClass}__container`}>
                    <h1 class="ux-text-title ux-text-title--big ux-u-text-align--center">
                        {t("serviceCase.proactiveCaseClosure.title")}
                    </h1>
                    <p class="ux-text-normal ux-u-text-align--center">{t("serviceCase.proactiveCaseClosure.instructions")}</p>
                    {this.questions.map((question) => (
                        <div class="ux-u-shadow ux-u-background-white ux-u-margin-vertical-20 ux-u-px-l ux-u-py-xs">
                            <QuestionRadio
                                t={t}
                                question={question}
                                answers={this.answers}
                                setAnswers={(answers) => {
                                    this.answers = answers;
                                }}
                            />
                            {question.comment && (
                                <QuestionComment
                                    label={t(question.comment.labelKey as TranslationKey) as unknown as string}
                                    placeholder={t(question.comment.placeholderKey as TranslationKey) as unknown as string}
                                    name={question.comment.name}
                                    answers={this.answers}
                                    setAnswers={(answers) => {
                                        this.answers = answers;
                                    }}
                                />
                            )}
                        </div>
                    ))}
                    {this.submitError && <p class={`${baseClass}__error ux-text-normal`}>{this.submitError}</p>}
                </div>
                <div class={`${baseClass}__button-container`}>
                    <ux-button onClick={() => this.cancel()} class={`${baseClass}__cancel-button`}>
                        {t("button.cancel")}
                    </ux-button>
                    <ux-button
                        onClick={() => this.submit()}
                        class={`${baseClass}__submit-button`}
                        variant="primary"
                        disabled={!this.complete || this.submitting}
                    >
                        {t("serviceCase.proactiveCaseClosure.question.submit")}
                    </ux-button>
                </div>
            </Host>
        );
    }
}

export interface ProactiveCaseClosureProps {
    /** SIS case ID */
    callId: string;
    /**
     * The locale to use for translations
     */
    locale: string;
}
